import { createClient } from '@supabase/supabase-js'

/**
 * A porta para o Supabase Auth (docs/areas/autenticacao.md, RN-AUT-01 e RN-AUT-20). O app só
 * conhece esta interface: em produção é o `supabase-js`; nos testes e nas stories, um falso.
 * Papel, clínica e nome nunca são guardados aqui: vêm de `GET /me`.
 */
export interface AuthClient {
  /** Token da sessão atual, renovado quando preciso; `null` se ninguém entrou. */
  getAccessToken(): Promise<string | null>
  /** Entra com e-mail e senha. Devolve o motivo quando a credencial não confere. */
  signIn(email: string, password: string): Promise<{ error: SignInError | null }>
  /**
   * Confere o código de 6 dígitos de "Entrar com código" e abre a sessão (RN-AUT-07). O pedido do
   * código não passa por aqui: é `POST /access/code`, com resposta neutra (docs/api/acesso.md).
   */
  verifyCode(email: string, code: string): Promise<{ error: CodeError | null }>
  signOut(): Promise<void>
  /** Pede um token novo (depois de trocar a clínica ativa, os claims mudam). */
  refresh(): Promise<void>
  /** Avisa quando a pessoa entra ou sai. Devolve a função que cancela a escuta. */
  onChange(listener: (isSignedIn: boolean) => void): () => void
}

/**
 * O que o app lê do token (docs/api/me.md): a marca de troca obrigatória da senha (RT-04) e quando a
 * pessoa provou a posse do e-mail (entrou por código, RN-AUT-07). Papel e clínica vêm de `GET /me`.
 */
export interface TokenClaims {
  mustChangePassword: boolean
  /**
   * Instante (ms) em que a pessoa entrou por código nesta sessão; `null` se entrou com senha. Nos 10
   * minutos seguintes, `PUT /me/password` aceita a senha nova sem a atual.
   */
  codeSignInAt: number | null
}

/** Os métodos que provam a posse do e-mail, como a API os lê (`_shared/auth/password.ts`). */
const EMAIL_AUTH_METHODS = new Set(['otp', 'magiclink', 'recovery', 'invite'])

/** Por quanto tempo, depois do código, a senha nova dispensa a atual (RN-AUT-07). */
export const CODE_WINDOW_MS = 10 * 60 * 1000

const NO_CLAIMS: TokenClaims = { mustChangePassword: false, codeSignInAt: null }

/**
 * Lê `app_metadata.must_change_password` e `amr` do token. Com a marca, a API recusa tudo menos
 * `PUT /me/password`, inclusive `GET /me`: por isso o app decide pelo token, sem chamar a API.
 * Token ilegível vale como "sem marca" e "entrou com senha"; quem decide de verdade é a API.
 */
export function readTokenClaims(token: string | null): TokenClaims {
  try {
    const payload = token?.split('.')[1]
    if (!payload) return NO_CLAIMS
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      app_metadata?: { must_change_password?: unknown }
      amr?: unknown
    }
    const entries = Array.isArray(json.amr)
      ? (json.amr as ({ method?: unknown; timestamp?: unknown } | null)[])
      : []
    const provedAt = entries.flatMap((entry) =>
      typeof entry?.method === 'string' &&
      EMAIL_AUTH_METHODS.has(entry.method) &&
      typeof entry.timestamp === 'number'
        ? [entry.timestamp * 1000]
        : [],
    )
    return {
      mustChangePassword: json.app_metadata?.must_change_password === true,
      codeSignInAt: provedAt.length > 0 ? Math.max(...provedAt) : null,
    }
  } catch {
    return NO_CLAIMS
  }
}

export interface SignInError {
  /** `invalid_credentials`, `rate_limited` ou `refused` (o Auth Hook recusou, com mensagem). */
  kind: 'invalid_credentials' | 'rate_limited' | 'refused'
  /** A frase que o Auth Hook mandou, quando a recusa veio dele (RN-AUT-02). */
  message: string | null
}

export interface CodeError {
  /**
   * `invalid_code`: o código não confere ou venceu; `rate_limited`: tentativas demais; `refused`: o
   * Auth Hook recusou a conta (bloqueada, arquivada, sem clínica), com a frase dele.
   */
  kind: 'invalid_code' | 'rate_limited' | 'refused'
  message: string | null
}

export function createSupabaseAuth(url: string, anonKey: string): AuthClient {
  const supabase = createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
  })
  return {
    async getAccessToken() {
      const { data } = await supabase.auth.getSession()
      return data.session?.access_token ?? null
    },
    async signIn(email, password) {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (!error) return { error: null }
      if (error.status === 429) return { error: { kind: 'rate_limited', message: null } }
      if (error.code === 'invalid_credentials' || error.status === 400) {
        return { error: { kind: 'invalid_credentials', message: null } }
      }
      return { error: { kind: 'refused', message: error.message || null } }
    },
    async verifyCode(email, code) {
      const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' })
      if (!error) return { error: null }
      if (error.status === 429) return { error: { kind: 'rate_limited', message: null } }
      // Código errado, vencido ou de e-mail que não existe: o Supabase responde igual (`otp_expired`).
      if (
        error.code === 'otp_expired' ||
        error.code === 'validation_failed' ||
        error.status === 400
      ) {
        return { error: { kind: 'invalid_code', message: null } }
      }
      return { error: { kind: 'refused', message: error.message || null } }
    },
    async signOut() {
      // Só a sessão deste aparelho: as outras seguem até vencer (RN-AUT-03).
      await supabase.auth.signOut({ scope: 'local' })
    },
    async refresh() {
      await supabase.auth.refreshSession()
    },
    onChange(listener) {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'INITIAL_SESSION') {
          listener(Boolean(session))
        }
      })
      return () => data.subscription.unsubscribe()
    },
  }
}
